/**
 * IC / OpAmp / MOSFET / BJT — anything with > 2 pins or a complex
 * symbol. Renders as a labelled rectangle with the refdes inside and a
 * value/MPN line below. Per-pin labels are V2 work — would require
 * propagating each Pin's position from the ELK layout.
 *
 * For chat-bubble previews this is a deliberate simplification: the
 * IC's identity (refdes + value/MPN) is what the user reads; pin
 * details belong on the dedicated canvas / detail panel surfaces.
 */
import { useTheme } from "@mui/material";
import type { NodeProps } from "@xyflow/react";

import { SymbolFrame } from "./SymbolFrame";
import { LABEL_STYLE, SYMBOL_STROKE_WIDTH, symbolColors } from "./symbolStyle";

interface ICData {
  refdes?: string;
  value?: string | null;
  /** Component.kind — picks the body decoration ("OPAMP" badge etc.). */
  kind?: string;
  pinCount?: number;
  highlight?: boolean;
}

export function ICNode({ data, width, height }: NodeProps) {
  const t = useTheme();
  const { stroke, fill, text, faint } = symbolColors(t);
  const d = data as ICData;
  // Inset by 2px so the stroke sits inside the bounding box and edges
  // don't overlap the body outline.
  const w = (width ?? 100) - 6;
  const h = (height ?? 60) - 12;
  return (
    <SymbolFrame
      // The IC body already shows the refdes / value inside, so we
      // skip the SymbolFrame label to avoid duplication.
      width={width}
      height={height}
      highlight={d.highlight}
    >
      <svg
        viewBox={`0 0 ${w} ${h}`}
        width={w}
        height={h}
        fill={fill}
        stroke={stroke}
        strokeWidth={SYMBOL_STROKE_WIDTH}
      >
        <rect x={1} y={1} width={w - 2} height={h - 2} rx={3} ry={3} />
        {/* Pin-1 indicator dot — top-left, conventional. */}
        <circle cx={6} cy={6} r={1.6} fill={stroke} stroke="none" />
        <text
          x={w / 2}
          y={h / 2 - 4}
          textAnchor="middle"
          fill={text}
          stroke="none"
          {...LABEL_STYLE}
          fontWeight={600}
          fontSize={11}
        >
          {d.refdes ?? ""}
        </text>
        {d.value && (
          <text
            x={w / 2}
            y={h / 2 + 9}
            textAnchor="middle"
            fill={faint}
            stroke="none"
            {...LABEL_STYLE}
            fontSize={9}
          >
            {d.value.length > 16 ? `${d.value.slice(0, 14)}…` : d.value}
          </text>
        )}
      </svg>
    </SymbolFrame>
  );
}
